import { collectTopicKeys, evaluateTrigger } from '@wit-support/pipeline';
import { nodeSpec } from '../../graph/node-spec.js';
import { reachableInOrder } from '../../graph/numbering.js';
import { MAX_OVERLAP_TOPICS } from '../../graph/overlap.js';
export const statusCounts = (list) => {
    const enabled = list.filter((p) => p.enabled).length;
    return { enabled, disabled: list.length - enabled, all: list.length };
};
/**
 * The filter the page opens with: «Включённые» (the mockup's default — the
 * disabled list is mostly old copies), unless nothing is enabled, in which case
 * opening on an empty list would hide every pipeline the user has.
 */
export const defaultStatusFilter = (list) => list.some((p) => p.enabled) ? 'enabled' : 'all';
const uniqueTopicKeys = (trigger) => trigger === null ? [] : [...new Set(collectTopicKeys(trigger))];
/**
 * Case-insensitive search over the name, the key and the trigger's topics (both
 * display names and keys — operators know topics by name, admins by key).
 * A blank query matches everything.
 */
export const matchesSearch = (p, query, topicName) => {
    const q = query.trim().toLocaleLowerCase('ru');
    if (q === '')
        return true;
    const haystack = [p.name, p.key, ...uniqueTopicKeys(p.trigger).flatMap((k) => [k, topicName(k)])];
    return haystack.some((s) => s.toLocaleLowerCase('ru').includes(q));
};
export const filterPipelines = (list, status, query, topicName) => list.filter((p) => (status === 'all' || p.enabled === (status === 'enabled')) &&
    matchesSearch(p, query, topicName));
/** Step count shown on the card: every node of the definition (same base as the step numbers). */
export const stepCount = (p) => Object.keys(p.definition.nodes).length;
/** Categories of the reachable steps in step-number order — the card's mini strip. */
export const stepCategories = (p) => reachableInOrder(p.definition).flatMap((id) => {
    const node = p.definition.nodes[id];
    return node ? [nodeSpec(node).category] : [];
});
/**
 * Does topic `key` switch this trigger ON — is there a classification without
 * `key` on which the trigger stays silent but fires once `key` is added?
 *
 * That is «this pipeline starts because of topic X»: a pipeline that needs
 * «Возврат» AND «Угроза жалобы» is listed under both, one that EXCLUDES a topic is
 * not listed under it, and a pipeline that fires on anything (`always`) is not
 * listed under any topic — it has its own group. Exact, by enumerating subsets of
 * the trigger's own topics (other topics cannot change its value); above
 * {@link MAX_OVERLAP_TOPICS} it falls back to «mentioned at all».
 */
export const topicActivates = (trigger, key) => {
    const others = uniqueTopicKeys(trigger).filter((k) => k !== key);
    if (others.length + 1 > MAX_OVERLAP_TOPICS)
        return uniqueTopicKeys(trigger).includes(key);
    const total = 1 << others.length;
    for (let mask = 0; mask < total; mask++) {
        const labels = new Set(others.filter((_, i) => (mask & (1 << i)) !== 0));
        if (evaluateTrigger(trigger, labels))
            continue;
        labels.add(key);
        if (evaluateTrigger(trigger, labels))
            return true;
    }
    return false;
};
/**
 * «По темам»: topic → the pipelines that topic starts. Topic groups are ordered
 * by display name (Russian collation) and only listed when non-empty; then
 * «При любой классификации» (triggers true on an empty label set — `always`,
 * or a bare «кроме»), then «Только вручную» (no trigger). A pipeline may appear
 * in several topic groups; within a group the input order is kept.
 */
export const groupByTopic = (list, topicName) => {
    const byTopic = new Map();
    const always = [];
    const manual = [];
    for (const p of list) {
        if (p.trigger === null) {
            manual.push(p);
            continue;
        }
        if (evaluateTrigger(p.trigger, new Set())) {
            always.push(p);
            continue;
        }
        for (const key of uniqueTopicKeys(p.trigger)) {
            if (!topicActivates(p.trigger, key))
                continue;
            byTopic.set(key, [...(byTopic.get(key) ?? []), p]);
        }
    }
    const groups = [...byTopic.entries()]
        .map(([topicKey, pipelines]) => ({
        kind: 'topic',
        topicKey,
        title: topicName(topicKey),
        pipelines,
    }))
        .sort((a, b) => a.title.localeCompare(b.title, 'ru'));
    if (always.length > 0)
        groups.push({ kind: 'always', title: 'При любой классификации', pipelines: always });
    if (manual.length > 0)
        groups.push({ kind: 'manual', title: 'Только вручную', pipelines: manual });
    return groups;
};
//# sourceMappingURL=model.js.map